// Page de personnalisation de la boutique - Logo, couleurs, style
import { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Palette, Upload, Eye, Save, RefreshCw, Check, X, Image as ImageIcon
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

// Couleurs prédéfinies pour le secteur bâtiment
const PRESET_COLORS = [
  { name: 'Orange BatiAssist', value: '#F97316' },
  { name: 'Bleu Professionnel', value: '#2563EB' },
  { name: 'Vert Écologique', value: '#16A34A' },
  { name: 'Rouge Passion', value: '#DC2626' },
  { name: 'Violet Moderne', value: '#7C3AED' },
  { name: 'Cyan Tech', value: '#0891B2' },
  { name: 'Jaune Chantier', value: '#EAB308' },
  { name: 'Gris Élégant', value: '#4B5563' },
];

export default function BoutiqueDesignPage() {
  const { user } = useOutletContext() || {};
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const [config, setConfig] = useState({
    shop_name: '',
    slug: '',
    tagline: '',
    description: '',
    logo: '',
    primary_color: '#F97316',
    secondary_color: '#1E293B',
    hero_images: [],
    phone: '',
    email: '',
    address: '',
    city: '',
    postal_code: ''
  });

  const token = localStorage.getItem('clientToken');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/vendor/shop-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(prev => ({ ...prev, ...data }));
      }
    } catch (e) {
      console.error('Error fetching config:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // For demo, create object URL
      // In production, upload to cloud storage
      const url = URL.createObjectURL(file);
      setConfig({ ...config, logo: url });
      toast.success('Logo chargé !');
    }
  };

  const generateSlug = () => {
    const slug = config.shop_name
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    setConfig({ ...config, slug });
  };

  const handleSave = async () => {
    if (!config.shop_name) {
      toast.error('Le nom de la boutique est obligatoire');
      return;
    }

    if (!config.slug) {
      generateSlug();
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/vendor/shop-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(config)
      });

      if (res.ok) {
        toast.success('Configuration sauvegardée !');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Personnalisation</h1>
          <p className="text-gray-500 mt-1">
            Crée l'identité visuelle de ta boutique
          </p>
        </div>
        
        <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
          {saving ? (
            <RefreshCw size={18} className="mr-2 animate-spin" />
          ) : (
            <Save size={18} className="mr-2" />
          )}
          Sauvegarder
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Form */}
        <div className="space-y-6">
          {/* Identité */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Identité de la boutique</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom de la boutique *
                </label>
                <Input
                  value={config.shop_name}
                  onChange={(e) => setConfig({ ...config, shop_name: e.target.value })}
                  placeholder="Ex: Plomberie Dupont"
                  data-testid="shop-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL de la boutique
                </label>
                <div className="flex gap-2">
                  <div className="flex-1 flex items-center bg-gray-100 rounded-lg px-3">
                    <span className="text-gray-500 text-sm">/boutique/</span>
                    <Input
                      value={config.slug}
                      onChange={(e) => setConfig({ ...config, slug: e.target.value })}
                      className="border-0 bg-transparent focus-visible:ring-0"
                      placeholder="plomberie-dupont"
                    />
                  </div>
                  <Button variant="outline" onClick={generateSlug} type="button">
                    <RefreshCw size={16} />
                  </Button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Slogan / Tagline
                </label>
                <Input
                  value={config.tagline}
                  onChange={(e) => setConfig({ ...config, tagline: e.target.value })}
                  placeholder="Ex: Votre expert plomberie depuis 1995"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <Textarea
                  value={config.description}
                  onChange={(e) => setConfig({ ...config, description: e.target.value })}
                  placeholder="Présente ton activité en quelques lignes..."
                  rows={4}
                />
              </div>
            </div>
          </motion.div>

          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Logo</h2>
            
            <div className="flex items-start gap-6">
              <div
                className="w-32 h-32 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50 overflow-hidden cursor-pointer hover:border-orange-400 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                {config.logo ? (
                  <img src={config.logo} alt="Logo" className="w-full h-full object-contain" />
                ) : (
                  <div className="text-center">
                    <Upload size={32} className="mx-auto text-gray-400 mb-2" />
                    <span className="text-sm text-gray-500">Cliquez pour uploader</span>
                  </div>
                )}
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />
              
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-2">
                  Recommandations :
                </p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• Format carré (1:1)</li>
                  <li>• Taille min. 200x200px</li>
                  <li>• PNG ou JPG</li>
                  <li>• Fond transparent idéal</li>
                </ul>
                
                {config.logo && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => setConfig({ ...config, logo: '' })}
                  >
                    <X size={14} className="mr-1" />
                    Supprimer
                  </Button>
                )}
              </div>
            </div>
          </motion.div>

          {/* Couleurs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Couleurs</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Couleur principale
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {PRESET_COLORS.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => setConfig({ ...config, primary_color: color.value })}
                      className={`w-10 h-10 rounded-lg border-2 transition-all ${
                        config.primary_color === color.value
                          ? 'border-gray-900 scale-110'
                          : 'border-transparent hover:scale-105'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    >
                      {config.primary_color === color.value && (
                        <Check size={20} className="text-white mx-auto" />
                      )}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={config.primary_color}
                    onChange={(e) => setConfig({ ...config, primary_color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={config.primary_color}
                    onChange={(e) => setConfig({ ...config, primary_color: e.target.value })}
                    className="w-32 font-mono"
                    placeholder="#F97316"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Couleur secondaire
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={config.secondary_color}
                    onChange={(e) => setConfig({ ...config, secondary_color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={config.secondary_color}
                    onChange={(e) => setConfig({ ...config, secondary_color: e.target.value })}
                    className="w-32 font-mono"
                    placeholder="#1E293B"
                  />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Coordonnées</h2>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <Input
                  value={config.phone}
                  onChange={(e) => setConfig({ ...config, phone: e.target.value })}
                  placeholder="06 12 34 56 78"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <Input
                  type="email"
                  value={config.email}
                  onChange={(e) => setConfig({ ...config, email: e.target.value })}
                  placeholder="contact@exemple.fr"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                <Input
                  value={config.address}
                  onChange={(e) => setConfig({ ...config, address: e.target.value })}
                  placeholder="123 rue de la République"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                <Input
                  value={config.city}
                  onChange={(e) => setConfig({ ...config, city: e.target.value })}
                  placeholder="Lyon"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code postal</label>
                <Input
                  value={config.postal_code}
                  onChange={(e) => setConfig({ ...config, postal_code: e.target.value })}
                  placeholder="69000"
                />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right Column - Preview */}
        <div className="lg:sticky lg:top-6 space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              <Eye size={20} className="inline mr-2" />
              Aperçu en direct
            </h2>
            
            {/* Mini Preview */}
            <div className="border rounded-lg overflow-hidden">
              {/* Header Preview */}
              <div 
                className="h-24 flex items-end p-4"
                style={{ backgroundColor: config.primary_color }}
              >
                <div className="flex items-center gap-3">
                  {config.logo ? (
                    <img src={config.logo} alt="" className="w-12 h-12 rounded-lg bg-white p-1 object-contain" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-white/20 flex items-center justify-center">
                      <ImageIcon size={24} className="text-white/60" />
                    </div>
                  )}
                  <div className="text-white">
                    <h3 className="font-bold">{config.shop_name || 'Nom de ta boutique'}</h3>
                    <p className="text-sm opacity-80">{config.tagline || 'Votre slogan ici'}</p>
                  </div>
                </div>
              </div>
              
              {/* Content Preview */}
              <div className="p-4 bg-gray-50">
                <div className="space-y-2">
                  <div 
                    className="h-3 rounded w-3/4"
                    style={{ backgroundColor: config.secondary_color, opacity: 0.2 }}
                  ></div>
                  <div 
                    className="h-3 rounded w-1/2"
                    style={{ backgroundColor: config.secondary_color, opacity: 0.1 }}
                  ></div>
                </div>
                
                <div className="flex gap-2 mt-4">
                  <div 
                    className="px-4 py-2 rounded-lg text-white text-sm font-medium"
                    style={{ backgroundColor: config.primary_color }}
                  >
                    Demander un devis
                  </div>
                  <div 
                    className="px-4 py-2 rounded-lg text-sm font-medium border"
                    style={{ borderColor: config.primary_color, color: config.primary_color }}
                  >
                    Contacter
                  </div>
                </div>
              </div>
            </div>
            
            <p className="text-sm text-gray-500 mt-4 text-center">
              Les modifications sont visibles en temps réel
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
